import { createAsyncAction } from 'typesafe-actions';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { Store } from 'store/storeTypes';
import { makeApiUrl } from 'actions/_shared/utils';
import apiAction from 'actions/_shared/apiAction';
import {
  SaveProductionHoursConfigurationRequest,
  ProductionHoursConfigurationApiResponse,
  ProductionHoursConfigurationResponse,
  ProductionHoursMatrixResponse,
  ProductionHoursHolidaysResponse,
  SaveProductionHoursHolidayRequest,
} from 'components/WorkingHours/utils/productionHoursApiTypes';
import {
  mapConfigurationResponse,
  ConfigurationBySystemGroupType,
} from 'components/WorkingHours/utils/productionHoursConfigurationResponseMapper';
import { WorkingDay } from 'components/WorkingHours/WorkingHours.types';
import {
  setWorkingHoursConfigurationFromApi,
  setWorkingHoursSiteConfigurationFromApi,
} from 'actions/workingHoursActions';
import {
  setHolidaysFromApi,
  cancelDeleteHoliday,
  discardDraftHoliday,
} from 'actions/holidaysActions';
import { toApiDateTime } from 'components/WorkingHours/utils/holidayDateUtils';
import { enqueueNotification } from 'actions/uiActions';

/**
 * Production Hours backend endpoints (site working-hours configuration, the
 * site x system-type configuration matrix, and holidays/days-off).
 *
 * `configuration` (GET/POST), `matrix` (GET) and `holidays` (GET/POST/DELETE) are typed
 * against confirmed response shapes (see productionHoursApiTypes.ts). `calendar` is still
 * typed loosely (`any`), pending its contract. `holidays/suggestions` (public holiday
 * catalog integration) is intentionally not implemented yet.
 */

// GET /api/production-hours/configuration
export const getProductionHoursConfigurationAC = createAsyncAction(
  'GET_PRODUCTION_HOURS_CONFIGURATION_STARTED',
  'GET_PRODUCTION_HOURS_CONFIGURATION_SUCCESS',
  'GET_PRODUCTION_HOURS_CONFIGURATION_FAILED'
)<Record<string, unknown> | undefined, ProductionHoursConfigurationApiResponse, Error>();

// `silent` skips dispatching setWorkingHoursConfigurationFromApi (used when fetching several
// sites in parallel just to compare their schedules — see
// getProductionHoursConfigurationForSitesRequest below, which merges the results itself
// instead of letting each individual response clobber the shared "current" days/config).
// `onResult` always fires (silent or not) so that caller can collect the mapped response.
export const getProductionHoursConfigurationRequest = (
  params?: Record<string, unknown>,
  options?: {
    silent?: boolean;
    onResult?: (
      mapped: { bySystemGroupType: ConfigurationBySystemGroupType; isMixed: boolean },
      data: ProductionHoursConfigurationApiResponse
    ) => void;
  }
) => {
  const url = makeApiUrl('/api/production-hours/configuration');
  return apiAction({
    request: { url, params },
    allowConcurrentRequests: options?.silent,
    logic: {
      onStarted: dispatch => dispatch(getProductionHoursConfigurationAC.request(params)),
      onSuccess: (data, dispatch) => {
        dispatch(getProductionHoursConfigurationAC.success(data));
        const mapped = mapConfigurationResponse(data);
        options?.onResult?.(mapped, data);
        if (!options?.silent) {
          dispatch(setWorkingHoursConfigurationFromApi(mapped));
        }
      },
      onFailed: (error, dispatch) => dispatch(getProductionHoursConfigurationAC.failure(error)),
    },
  });
};

// One request covers every site and returns one entry per site, tagged with its siteName. There is
// no cross-site "isMixed" field server-side the way there is for system group types, so the
// schedules are compared client-side.
const daysScheduleSignature = (days: WorkingDay[]) =>
  JSON.stringify(
    days.map(day => ({
      enabled: day.enabled,
      slots: [...day.slots]
        .map(slot => [slot.startMinutes, slot.endMinutes])
        .sort((a, b) => a[0] - b[0]),
    }))
  );

export const getProductionHoursConfigurationForSitesRequest = (params: {
  siteNames: string[];
  systemGroupTypes: string[];
}) => async (dispatch: ThunkDispatch<Store, any, Action>) => {
  const { siteNames, systemGroupTypes } = params;
  if (siteNames.length <= 1) {
    dispatch(
      getProductionHoursConfigurationRequest({
        SiteNames: siteNames,
        SystemGroupTypes: systemGroupTypes,
      })
    );
    return;
  }

  let entries: ProductionHoursConfigurationResponse[] = [];
  await dispatch(
    getProductionHoursConfigurationRequest(
      { SiteNames: siteNames, SystemGroupTypes: systemGroupTypes },
      {
        silent: true,
        onResult: (_mapped, data) => {
          entries = Array.isArray(data) ? data : [data];
        },
      }
    )
  );
  if (entries.length === 0) return;

  const bySite: Partial<Record<string, WorkingDay[]>> = {};
  entries.forEach(entry => {
    // The server resolves site names case-insensitively and echoes them as stored.
    const responseSiteName = entry.configurationsPerSystemGroupType?.[0]?.siteName?.toLowerCase();
    const siteName = siteNames.find(name => name.toLowerCase() === responseSiteName);
    if (!siteName) return;
    const { bySystemGroupType } = mapConfigurationResponse(entry);
    const preferredType = systemGroupTypes.find(type => bySystemGroupType[type]);
    bySite[siteName] = (preferredType && bySystemGroupType[preferredType]) || [];
  });

  const signatures = new Set(Object.values(bySite).map(daysScheduleSignature));
  dispatch(setWorkingHoursSiteConfigurationFromApi({ bySite, isMixed: signatures.size > 1 }));
};

// POST /api/production-hours/configuration
export const saveProductionHoursConfigurationAC = createAsyncAction(
  'SAVE_PRODUCTION_HOURS_CONFIGURATION_STARTED',
  'SAVE_PRODUCTION_HOURS_CONFIGURATION_SUCCESS',
  'SAVE_PRODUCTION_HOURS_CONFIGURATION_FAILED'
)<SaveProductionHoursConfigurationRequest, ProductionHoursConfigurationApiResponse, Error>();

export const saveProductionHoursConfigurationRequest = (
  data: SaveProductionHoursConfigurationRequest,
  options?: { onError?: () => void }
) => {
  const url = makeApiUrl('/api/production-hours/configuration');
  return apiAction({
    request: { url, method: 'POST', data },
    logic: {
      onStarted: dispatch => dispatch(saveProductionHoursConfigurationAC.request(data)),
      onSuccess: (responseData, dispatch) => {
        dispatch(saveProductionHoursConfigurationAC.success(responseData));
        const { bySystemGroupType, isMixed } = mapConfigurationResponse(responseData);
        dispatch(setWorkingHoursConfigurationFromApi({ bySystemGroupType, isMixed }));
      },
      onFailed: (error, dispatch) => {
        dispatch(saveProductionHoursConfigurationAC.failure(error));
        options?.onError?.();
      },
    },
  });
};

// The backend accepts every selected site in a single request (`siteNames: string[]`), so
// saving for multiple sites at once is just this one POST — no more per-site fan-out. Throws
// on failure so the caller can show a single success/error indication.
export const saveProductionHoursConfigurationForSitesRequest = (
  payload: SaveProductionHoursConfigurationRequest
) => async (dispatch: ThunkDispatch<Store, any, Action>) => {
  let failed = false;
  await dispatch(
    saveProductionHoursConfigurationRequest(payload, {
      onError: () => {
        failed = true;
      },
    })
  );
  // The site x system-type matrix (isSet: true/false) can only change as a side effect of
  // saving a schedule - re-fetch it so the machine filter's "no production hours" greyed-out
  // state (see sitesWithProductionHoursSetSelector) is never stale. Refreshed regardless of
  // success/failure since a partial save can still have changed it.
  dispatch(getProductionHoursMatrixRequest());
  if (failed) throw new Error('Failed to save production hours configuration');
};

// GET /api/production-hours/matrix — no request params.
export const getProductionHoursMatrixAC = createAsyncAction(
  'GET_PRODUCTION_HOURS_MATRIX_STARTED',
  'GET_PRODUCTION_HOURS_MATRIX_SUCCESS',
  'GET_PRODUCTION_HOURS_MATRIX_FAILED'
)<undefined, ProductionHoursMatrixResponse, Error>();

export const getProductionHoursMatrixRequest = () => {
  const url = makeApiUrl('/api/production-hours/matrix');
  return apiAction({
    request: { url },
    logic: {
      onStarted: dispatch => dispatch(getProductionHoursMatrixAC.request()),
      onSuccess: (data, dispatch) => dispatch(getProductionHoursMatrixAC.success(data)),
      onFailed: (error, dispatch) => dispatch(getProductionHoursMatrixAC.failure(error)),
    },
  });
};

// GET /api/production-hours/calendar
export const getProductionHoursCalendarAC = createAsyncAction(
  'GET_PRODUCTION_HOURS_CALENDAR_STARTED',
  'GET_PRODUCTION_HOURS_CALENDAR_SUCCESS',
  'GET_PRODUCTION_HOURS_CALENDAR_FAILED'
)<any, any, Error>();

export const getProductionHoursCalendarRequest = (params?: Record<string, unknown>) => {
  const url = makeApiUrl('/api/production-hours/calendar');
  return apiAction({
    request: { url, params },
    logic: {
      onStarted: dispatch => dispatch(getProductionHoursCalendarAC.request(params)),
      onSuccess: (data, dispatch) => dispatch(getProductionHoursCalendarAC.success(data)),
      onFailed: (error, dispatch) => dispatch(getProductionHoursCalendarAC.failure(error)),
    },
  });
};

// GET /api/production-hours/holidays — one request for every selected site.
export const getProductionHoursHolidaysAC = createAsyncAction(
  'GET_PRODUCTION_HOURS_HOLIDAYS_STARTED',
  'GET_PRODUCTION_HOURS_HOLIDAYS_SUCCESS',
  'GET_PRODUCTION_HOURS_HOLIDAYS_FAILED'
)<{ siteNames: string[] }, ProductionHoursHolidaysResponse, Error>();

export const getProductionHoursHolidaysRequest = (params: { siteNames: string[] }) => {
  const url = makeApiUrl('/api/production-hours/holidays');
  return apiAction({
    request: { url, params },
    logic: {
      onStarted: dispatch => dispatch(getProductionHoursHolidaysAC.request(params)),
      onSuccess: (data, dispatch) => {
        dispatch(getProductionHoursHolidaysAC.success(data));
        dispatch(setHolidaysFromApi({ response: data }));
      },
      onFailed: (error, dispatch) => dispatch(getProductionHoursHolidaysAC.failure(error)),
    },
  });
};

// POST /api/production-hours/holidays — creates (id: 0, for every given site) or updates
// (existing id, owning site only) a single holiday; the response echoes back the full lists.
export const saveProductionHoursHolidayAC = createAsyncAction(
  'SAVE_PRODUCTION_HOURS_HOLIDAY_STARTED',
  'SAVE_PRODUCTION_HOURS_HOLIDAY_SUCCESS',
  'SAVE_PRODUCTION_HOURS_HOLIDAY_FAILED'
)<SaveProductionHoursHolidayRequest, ProductionHoursHolidaysResponse, Error>();

// `silent` skips the bySite/draft sync (used when batch-saving several holidays, where the
// batch itself re-syncs once at the end instead). `allowConcurrent` lets several of these
// run at once instead of each cancelling the previous in-flight one (same URL for every
// call) — needed for that same batch case.
export const saveProductionHoursHolidayRequest = (
  data: SaveProductionHoursHolidayRequest,
  options?: { silent?: boolean; allowConcurrent?: boolean; onError?: () => void }
) => {
  const url = makeApiUrl('/api/production-hours/holidays');
  return apiAction({
    request: { url, method: 'POST', data },
    allowConcurrentRequests: options?.allowConcurrent,
    logic: {
      onStarted: dispatch => dispatch(saveProductionHoursHolidayAC.request(data)),
      onSuccess: (responseData, dispatch) => {
        dispatch(saveProductionHoursHolidayAC.success(responseData));
        if (!options?.silent) {
          dispatch(setHolidaysFromApi({ response: responseData }));
        }
      },
      onFailed: (error, dispatch) => {
        dispatch(saveProductionHoursHolidayAC.failure(error));
        options?.onError?.();
      },
    },
  });
};

// DELETE /api/production-hours/holidays/{holidayId} — same echoed full-list response shape.
export const deleteProductionHoursHolidayAC = createAsyncAction(
  'DELETE_PRODUCTION_HOURS_HOLIDAY_STARTED',
  'DELETE_PRODUCTION_HOURS_HOLIDAY_SUCCESS',
  'DELETE_PRODUCTION_HOURS_HOLIDAY_FAILED'
)<number, ProductionHoursHolidaysResponse, Error>();

// `silent` — see saveProductionHoursHolidayRequest above. Each DELETE already has a
// distinct URL (id is in the path), so these are always safe to run concurrently.
export const deleteProductionHoursHolidayRequest = (
  holidayId: number,
  options?: { silent?: boolean; onError?: () => void }
) => {
  const url = makeApiUrl(`/api/production-hours/holidays/${holidayId}`);
  return apiAction({
    request: { url, method: 'DELETE' },
    logic: {
      onStarted: dispatch => dispatch(deleteProductionHoursHolidayAC.request(holidayId)),
      onSuccess: (responseData, dispatch) => {
        dispatch(deleteProductionHoursHolidayAC.success(responseData));
        if (!options?.silent) {
          dispatch(setHolidaysFromApi({ response: responseData }));
        }
      },
      onFailed: (error, dispatch) => {
        dispatch(deleteProductionHoursHolidayAC.failure(error));
        options?.onError?.();
      },
    },
  });
};

// Fires the actual DELETE only once the user confirms in the "are you sure?" popup —
// requestDeleteHoliday only opens that popup and doesn't touch the server. If the item was
// never saved (added this session, not yet in bySite), there's nothing to delete server-side —
// just drop it from the draft.
export const confirmDeleteHolidayRequest = () => async (
  dispatch: ThunkDispatch<Store, any, Action>,
  getState: () => Store
) => {
  const { pendingDeleteId, activeSites, bySite } = getState().holidays;
  if (!pendingDeleteId) return;
  const isSaved = activeSites.some(site => (bySite[site] ?? []).some(b => b.id === pendingDeleteId));
  if (isSaved) {
    await dispatch(deleteProductionHoursHolidayRequest(Number(pendingDeleteId)));
  } else {
    dispatch(discardDraftHoliday({ id: pendingDeleteId }));
  }
  dispatch(cancelDeleteHoliday());
};

// GET /api/production-hours/holidays/suggestions — reserved for the public holiday
// catalog integration; not available yet, intentionally not implemented.

// "Save days off" — the modal edits a local draft list (calendar drag-select, name/repeat
// fields, per-item delete); this diffs that draft against the last-fetched server state for
// the open sites and replays it as a batch of single-holiday POST/DELETE calls, since the API
// only accepts one holiday per request (a new one is created for all its sites in one POST). All calls fire in parallel (Promise.all, `silent`
// so none of them touch bySite/draft on their own, `allowConcurrent` on the POSTs since they
// all share one URL and would otherwise cancel each other) — only once every one of them
// has resolved does a single GET re-sync the definitive list, so the UI updates exactly
// once, not per call. Leaves the modal open — the user closes it explicitly.
//
// apiAction swallows request failures internally (its dispatched thunk always resolves,
// never rejects), so Promise.all below can't detect a partial failure on its own — each
// call reports back through `onError` instead, and a distinct notification is shown if any
// of them failed. The final GET always re-syncs to the server's real state regardless, so a
// holiday that failed to save/delete simply shows up as still-unsaved/still-present rather
// than being lost or misreported.
export const saveHolidaysDraftRequest = () => async (
  dispatch: ThunkDispatch<Store, any, Action>,
  getState: () => Store
) => {
  const { draft, bySite, activeSites } = getState().holidays;
  const original = activeSites.flatMap(site => bySite[site] ?? []);
  const originalById = new Map(original.map(block => [block.id, block]));
  const draftIds = new Set(draft.map(block => block.id));

  const removed = original.filter(block => !draftIds.has(block.id));
  const upserted = draft.filter(block => {
    const prev = originalById.get(block.id);
    if (!prev) return true;
    return (
      prev.start !== block.start ||
      prev.end !== block.end ||
      prev.name !== block.name ||
      prev.repeat !== block.repeat
    );
  });

  if (removed.length === 0 && upserted.length === 0) return;

  let failureCount = 0;
  const onError = () => {
    failureCount += 1;
  };

  await Promise.all([
    ...removed.map(block =>
      dispatch(deleteProductionHoursHolidayRequest(Number(block.id), { silent: true, onError }))
    ),
    ...upserted.map(block => {
      const isNew = !originalById.has(block.id);
      return dispatch(
        saveProductionHoursHolidayRequest(
          {
            siteNames: block.siteNames,
            holiday: {
              id: isNew ? 0 : Number(block.id),
              startDate: toApiDateTime(block.start),
              endDate: toApiDateTime(block.end),
              name: block.name,
              repeatYearly: block.repeat,
            },
          },
          { silent: true, allowConcurrent: true, onError }
        )
      );
    }),
  ]);

  await dispatch(getProductionHoursHolidaysRequest({ siteNames: activeSites }));

  if (failureCount > 0) {
    const total = removed.length + upserted.length;
    dispatch(
      enqueueNotification(`${failureCount} of ${total} changes could not be saved — please try again.`, {
        variant: 'error',
      })
    );
  }
};
